import React from 'react'
import useAxios from 'axios-hooks'
import * as Icon from 'react-feather'
import styled from 'styled-components'
import classNames from 'classnames'
import { useSelector } from 'react-redux'
import {
  Nav,
  NavItem,
  NavLink as NavLinkReactstrap,
  TabContent,
  TabPane,
} from 'reactstrap'
import { FormattedMessage } from 'react-intl'

import { LoadableCard } from '~/components/commons'
import TemplateCard from './TemplateCard'

const EditTemplatesForm = ({ templateTab, setTemplateTab }) => {
  const [templates, setTemplates] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const accountId = useSelector(state => state.user?.profile?.account?.id)

  const [{ data }] = useAxios({
    url: `/accounts/${accountId}/templates`,
    method: 'GET',
  })

  function handleAddTemplate(category) {
    const newTemplates = [
      {
        id: 'temporary-id',
        category,
        content: '',
      },
      ...templates,
    ]
    setTemplates(newTemplates)
  }

  function handleRemove(id) {
    const newTemplates = templates.filter(template => template.id !== id)
    setTemplates(newTemplates)
  }

  function handleUpdate(id, template) {
    const newTemplates = JSON.parse(JSON.stringify(templates))
    const temporaryIndex = newTemplates.findIndex(temp => temp.id === id)

    if (temporaryIndex !== -1) {
      newTemplates[temporaryIndex] = template
    }

    setTemplates(newTemplates)
  }

  React.useEffect(() => {
    if (!data) return

    setIsLoading(false)

    setTemplates(data?.templates || [])
  }, [data])

  return (
    <LoadableCard className="p-3" loading={isLoading}>
      <Container>
        <StyledForm>
          <h2>
            <FormattedMessage id="Templates" />
          </h2>
          <Nav
            tabs
            className="nav-bottom mt-1"
            style={{ minWidth: '150px', boxShadow: 'none' }}
          >
            <NavItem>
              <NavLinkPane
                className={classNames({
                  active: templateTab === '1',
                })}
                onClick={() => setTemplateTab('1')}
              >
                <Icon.Star className="mr-1" />
                <FormattedMessage id="Reviews" />
              </NavLinkPane>
            </NavItem>
            <NavItem>
              <NavLinkPane
                className={classNames({
                  active: templateTab === '2',
                })}
                onClick={() => setTemplateTab('2')}
              >
                <Icon.HelpCircle className="mr-1" />
                <FormattedMessage id="Questions" />
              </NavLinkPane>
            </NavItem>
          </Nav>

          <TabContent activeTab={templateTab} className="mt-1 w-100">
            <StyledTabPane tabId="1" className="bg-transparent pl-0">
              <Container>
                <div className="templates-header">
                  <h3>
                    <FormattedMessage id="Reviews" />
                  </h3>
                  <button
                    onClick={() => handleAddTemplate('reviews')}
                    disabled={templates.find(
                      template =>
                        template.id === 'temporary-id' &&
                        template.category === 'reviews'
                    )}
                  >
                    <FormattedMessage id="Add Template" />
                  </button>
                </div>
                <div className="templates-list">
                  {templates
                    .filter(template => template.category === 'reviews')
                    .map(template => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        removeById={handleRemove}
                        updateById={handleUpdate}
                      />
                    ))}
                </div>
              </Container>
            </StyledTabPane>
            <StyledTabPane tabId="2" className="bg-transparent">
              <Container>
                <div className="templates-header">
                  <h3>
                    <FormattedMessage id="Reviews" />
                  </h3>
                  <button
                    onClick={() => handleAddTemplate('questions')}
                    disabled={templates.find(
                      template =>
                        template.id === 'temporary-id' &&
                        template.category === 'questions'
                    )}
                  >
                    <FormattedMessage id="Add Template" />
                  </button>
                </div>
                <div className="templates-list">
                  {templates
                    .filter(template => template.category === 'questions')
                    .map(template => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        removeById={handleRemove}
                        updateById={handleUpdate}
                      />
                    ))}
                </div>
              </Container>
            </StyledTabPane>
          </TabContent>
        </StyledForm>
      </Container>
    </LoadableCard>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const StyledForm = styled.div`
  h2 {
    color: #2c2c2c;
    font-family: Montserrat;
    font-size: 1.5rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    margin-bottom: 1.5rem;
  }

  button {
    width: fit-content;
    margin-top: 1rem;
    padding: 0.5rem 1rem;
    background-color: #61c478;
    border-radius: 0.4rem;
    border: none;
    color: white;

    &:disabled {
      opacity: 0.65;
    }
  }

  .templates-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      color: #2c2c2c;
      font-family: Montserrat;
      font-size: 1.125rem;
      font-style: normal;
      font-weight: 500;
      line-height: 100%;
      margin-bottom: 0;
    }

    button {
      background-color: #7367f0;
      color: white;
      border: none;
      padding: 0.5rem 1rem;
      border-radius: 0.25rem;
      margin-top: 0;
    }
  }

  .templates-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .nav-pane {
    overflow-y: initial !important;
  }
`

const NavLinkPane = styled(NavLinkReactstrap)`
  color: #626262 !important;

  &.active {
    font-weight: 600;
    color: #7367f0 !important;
    &::after {
      background-color: #556ee6 !important;
      transform: none !important;
      width: 100% !important;
      bottom: 0 !important;
      top: auto !important;
      left: 0 !important;
    }
  }
`

const StyledTabPane = styled(TabPane)`
  overflow: hidden !important;
`

export default EditTemplatesForm
